import { VenueOS, type VenueOSClient, type VenueOSContext } from '@mappedin/venueos-applet';
import type { MutableRefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

const INIT_TIMEOUT_MS = 10_000;

interface UseVenueOSReturn {
  client: VenueOSClient | null;
  clientRef: MutableRefObject<VenueOSClient | null>;
  context: VenueOSContext | null;
  isEmbedded: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * Initialize the VenueOS applet SDK and expose the live client + context.
 *
 * Mirrors the pattern used in `apps/web-applet/src/hooks/useVenueOS.ts` so
 * transit-app behaves the same way when run inside vs outside the VenueOS
 * shell. When `VenueOS.isEmbedded()` is false (e.g. plain `vite dev`), this
 * resolves immediately with a null client and the caller renders a
 * "must be embedded" notice.
 */
export function useVenueOS(): UseVenueOSReturn {
  const clientRef = useRef<VenueOSClient | null>(null);
  const contextHandlerRef = useRef<((ctx: VenueOSContext) => void) | null>(null);
  const [context, setContext] = useState<VenueOSContext | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isEmbedded = VenueOS.isEmbedded();

  useEffect(() => {
    let mounted = true;

    const initVenueOS = async (): Promise<void> => {
      if (!isEmbedded) {
        setIsLoading(false);
        setContext(null);
        return;
      }

      try {
        const client = await VenueOS.init({ timeout: INIT_TIMEOUT_MS });
        if (!mounted || !client) return;

        clientRef.current = client;
        setContext(client.context);
        client.setShellUI({ showVenueSelector: false });

        const handler = (newContext: VenueOSContext): void => {
          if (mounted) setContext(newContext);
        };
        contextHandlerRef.current = handler;
        client.on('context:updated', handler);
        setIsLoading(false);
      } catch (err: unknown) {
        if (!mounted) return;
        setError(err instanceof Error ? err.message : 'Failed to initialize VenueOS');
        setContext(null);
        setIsLoading(false);
      }
    };

    void initVenueOS();

    return () => {
      mounted = false;
      const client = clientRef.current;
      if (!client) return;
      const handler = contextHandlerRef.current;
      if (handler) {
        client.off('context:updated', handler);
        contextHandlerRef.current = null;
      }
      client.destroy();
      clientRef.current = null;
    };
  }, [isEmbedded]);

  return {
    client: clientRef.current,
    clientRef,
    context,
    isEmbedded,
    isLoading,
    error,
  };
}
